import { SKIP_GLOBAL_ERROR_HANDLING_FLAG } from '@/composables/ErrorHandler';
import router from '@/router';
import { RouterHelpers } from '@/router/routeHelpers';
import { useNavigationStore } from '@/stores/navigationStore';
import { appConfig } from '@/utils/appConfig';
import { loescheAnmeldestatus, setzeAnmeldestatus } from '@/utils/auth';
import { i18n } from '@/utils/i18n';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import dayjs from 'dayjs/esm/index.js';
import { RouteLocationNormalizedGeneric } from 'vue-router';
import type { ProblemDetails, ValidationProblemDetails } from './model';
import { ApiRouten } from './routen';

// Erweitere AxiosRequestConfig um unsere benutzerdefinierten Eigenschaften
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    _isRefreshRequest?: boolean;
    _retry?: boolean;
    _skipTransform?: boolean;
}

// Erstelle eine benutzerdefinierte axios-Instanz
const apiClient: AxiosInstance = axios.create({
    baseURL: appConfig.apiUrl,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'text/plain'
    },
    withCredentials: true, // Wichtig: Erlaubt das Senden von Cookies mit Anfragen
    transformRequest: [
        function (data) {
            // Wenn skipTransform gesetzt ist, Daten unverändert zurückgeben
            const config = this as CustomAxiosRequestConfig;
            if (config?._skipTransform) {
                return data;
            }

            if (!data) {
                return data;
            }

            // Multipart-Uploads dürfen nicht serialisiert werden
            if (data instanceof FormData) {
                return data;
            }

            // Standardverhalten von JSON.stringify bei Date:
            // -> Date.toJSON() -> Date.toISOString() -> UTC mit "Z"
            //    Beispiel: new Date(2016, 11, 31, 23, 0) =>
            //              "2016-12-31T22:00:00.000Z"
            //
            // Das führt zu Problemen, weil:
            // - Uhrzeit wird in UTC umgerechnet
            // - Server erwartet aber "naive" lokale DateTimes ohne Offset
            //   (z. B. "2016-12-31T23:00:00")
            //
            // Deshalb: Alle Date-Instanzen vor der Serialisierung
            // rekursiv durchgehen und manuell formatieren, sodass
            // sie als "YYYY-MM-DDTHH:mm:ss" (ohne Zeitzone) im JSON stehen.
            return JSON.stringify(replaceDates(data));
        }
    ]
});

// Rekursive Funktion, ersetzt alle Date-Instanzen
function replaceDates(value: any): any {
    if (value instanceof Date) {
        // Formatiere Datum/Zeit explizit ohne Offset oder "Z"
        return dayjs(value).format('YYYY-MM-DDTHH:mm:ss');
    }

    // Arrays berücksichtigen
    if (Array.isArray(value)) {
        return value.map(replaceDates);
    }

    // Verschachtelte Objekte berücksichtigen, z.B. Probe.VorlageProbe
    if (value && typeof value === 'object') {
        const copy: Record<string, any> = {};
        for (const [k, v] of Object.entries(value)) {
            copy[k] = replaceDates(v);
        }
        return copy;
    }
    return value;
}

/**
 * Erneuert das Authentifizierungstoken
 * @returns Promise<boolean> True, wenn die Erneuerung erfolgreich war, sonst false
 */
export const refreshToken = async (): Promise<AxiosResponse> => {
    // Verwende die bestehende apiClient-Instanz, aber markiere die Anfrage als Refresh-Anfrage,
    // um zu verhindern, dass der Interceptor sie behandelt
    const response: AxiosResponse = await apiClient.post(ApiRouten.AUTHENTIFIZIERUNG.REFRESH, {}, {
        _isRefreshRequest: true
    } as CustomAxiosRequestConfig);

    return response;
};

let laufenderRefreshVorgang: Promise<AxiosResponse> | null = null;

export const bavLimsAnmelden = async (to: RouteLocationNormalizedGeneric): Promise<void> => {
    await apiClient.post(ApiRouten.AUTHENTIFIZIERUNG.BAV_LIMS_ANMELDEN, null, { params: { token: to.query.token } });

    setzeAnmeldestatus(true);

    await router.replace(RouterHelpers.toDashboard());
};

// Füge einen Request-Interceptor hinzu, um den Accept-Language Header zu setzen
apiClient.interceptors.request.use(
    (config) => {
        // Setze den Accept-Language Header basierend auf der aktuellen Sprache
        // aktuelle Sprache aus Vue I18n
        config.headers['Accept-Language'] = i18n.global.locale.value;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Füge einen Response-Interceptor hinzu, um HTTP-Fehler zu behandeln
apiClient.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
        return response;
    },
    async (error: AxiosError): Promise<any> => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        // Wenn es sich um den Refresh-Token-Request selbst handelt und dieser fehlgeschlagen ist, abbrechen
        if (originalRequest._isRefreshRequest || originalRequest._retry) {
            return Promise.reject(error);
        }

        // Token-Refresh nur einmal versuchen
        if (error.response?.status === 401) {
            // Unauthorized - Token-Refresh-Logik
            if (router.currentRoute.value.path !== '/login') {
                // Markiere diese Anfrage als wiederholt
                originalRequest._retry = true;

                try {
                    // Mehrere Requests können bei abgelaufener Session gleichzeitig mit 401 scheitern.
                    // Beispiel: `ladeKundenDaten()` startet `FILIALEN` und `EINSTELLUNGEN` parallel.
                    // Jetzt würden beide Requests im Interceptor `refreshToken()` starten.
                    // Wenn das aufgrund von SlidingExpiration fehlschälgt, würden beide einen Logout mit returnUrl triggern und dadurch die returnUrl verschachteln (/login?returnUrl=/login?returnUrl=....)
                    // Deshalb warten wir jetzt, bis der laufende Refresh-Vorgang beendet ist.
                    if (!laufenderRefreshVorgang) {
                        laufenderRefreshVorgang = refreshToken()
                            .catch(async (refreshError: any) => {
                                // Flag am Fehler setzen, damit globale Handler diesen speziellen Fall ignorieren
                                refreshError[SKIP_GLOBAL_ERROR_HANDLING_FLAG] = true;

                                const problemDetails = refreshError.response?.data as ProblemDetails;
                                const fehlermeldung = problemDetails?.detail ?? refreshError.message;

                                // Beim forcierten Logout darf der ausstehende Änderungen Handler nicht dazwischenfunken
                                const navigationStore = useNavigationStore();
                                navigationStore.ausstehendeAenderungenPruefungUeberspringen = true;

                                // Wichtig, Fehler aus LIMS-452:
                                // ggf. die im NavigationStore zwischengespeicherte Ziel-URL verwenden, siehe Kommentar dort und beim router.beforeEach()
                                // Außerdem router.currentRoute.value.fullPath anstatt nur path verwenden, damit auch Query-Parameter erhalten bleiben, z. B. ?SchnellsucheProbenNr=12345678
                                const returnUrl = navigationStore.ausstehendeNavigationReturnUrl ?? router.currentRoute.value.fullPath;

                                await logout(returnUrl, false, true, fehlermeldung);

                                throw refreshError;
                            })
                            .finally(() => {
                                laufenderRefreshVorgang = null;
                            });
                    }

                    await laufenderRefreshVorgang;

                    // Bei Erfolg wurden neue Auth-Cookies ausgestellt
                    // jetzt kann der ursprüngliche Request nochmal ausgeführt werden
                    // Wichtig!
                    // SkipTransform verhindert, dass der Body erneut serialisiert wird (siehe Verwendung)
                    // Anderenfalls ginge das JSON kaputt und der Server würde 400 liefern.
                    originalRequest._skipTransform = true;
                    return apiClient(originalRequest);
                } catch (refreshError) {
                    // Wichtig! Ursprüngliche Codeausführung abbrechen damit keine Folgefehler entstehen!
                    throw refreshError;
                }
            } else {
                // Wenn wir bereits auf der Loginseite sind, nur den Authentifizierungsstatus löschen
                await logout(null, false, false);
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Abmeldefunktion, die den Logout-Endpunkt aufruft, um Cookies zu löschen
 * @param {string} returnUrl - Optionale URL, zu der nach erneuter Anmeldung zurückgekehrt werden soll
 */
export const logout = async (returnUrl?: string | null, serverLogoutDurchfuehren: boolean = true, navigiereZuLogin: boolean = true, logoutMessage?: string): Promise<void> => {
    try {
        if (serverLogoutDurchfuehren) {
            await apiClient.post(ApiRouten.AUTHENTIFIZIERUNG.LOGOUT);
        }
    } finally {
        loescheAnmeldestatus();

        if (navigiereZuLogin) {
            await router.replace({
                path: '/login',
                query: returnUrl ? { returnUrl } : undefined,
                state: logoutMessage ? { logoutMessage } : undefined
            });
        }

        const { useKundenbereichStore } = await import('@/stores/kundenbereichStore');
        useKundenbereichStore().resetKundenDaten();

        const { useProbenStore } = await import('@/stores/probenStore');
        useProbenStore().clearHistorie();
    }
};

export const getBlobRequestConfig = (): AxiosRequestConfig => ({
    responseType: 'blob',
    headers: { Accept: 'application/pdf, application/problem+json' }
});

export const getValidationProblemDetailsFromBlob = async (data: Blob): Promise<ValidationProblemDetails> => {
    const text = await data.text();
    return JSON.parse(text) as ValidationProblemDetails;
};

export const downloadFromBlobResponse = (response: AxiosResponse<Blob>): void => {
    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    if (!contentDisposition) {
        throw new Error('downloadFromBlobResponse: Content-Disposition Header fehlt');
    }

    // Erwartet filename* mit UTF-8 Encoding
    const match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (!match) {
        throw new Error('downloadFromBlobResponse: filename* (UTF-8) nicht gefunden');
    }

    const fileName = decodeURIComponent(match[1].replace(/["']/g, '').trim());

    const blob = response.data;
    if (!(blob instanceof Blob)) {
        throw new Error('downloadFromBlobResponse: response.data ist kein Blob');
    }

    const url = URL.createObjectURL(blob);
    try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        URL.revokeObjectURL(url);
    }
};

export default apiClient;
